import { Component, OnDestroy } from '@angular/core';
import { combineLatest, filter, forkJoin, map, take } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { ProcessingManager } from '@managers/processing.manager';
import { ProcessingStep } from '@models/ui/processing-step.model';
import { TableLikeBaseComponent } from '../../../table-like-base.component';
import { ActivatedRoute } from '@angular/router';
import { NotificationManager } from '@managers/notification.manager';
import { orderBy } from 'lodash-es';
import { SyncKnowledgeBaseRequest } from '@models/ui/job-request/sync-knowledge-base-request.model';
import { UserConfigsService } from "@services/user-configs.service";
import { rowTransitionAnimation } from "../animations";
import { IndexClient, PilotClient } from "@clients/api.client";

@Component({
	selector: 'lex-all-steps',
	templateUrl: './index-health-table.component.html',
	styleUrls: ['./index-health-table.component.scss'],
	animations: [rowTransitionAnimation],
	standalone: false
})
export class IndexHealthTableComponent extends TableLikeBaseComponent<CaseHealthView> implements OnDestroy {
	public selectedIds = [];
	public total: number;

	public columnsToDisplay = [
		'selecto-checko',
		'caseName',
		'docCount',
		'indexDocCount',
		'status'
	];
	public rowHeight$ = this.processingManager.currentRowHeight$;

	constructor(
		public processingManager: ProcessingManager,
		private activatedRoute: ActivatedRoute,
		private notificationManager: NotificationManager,
		private userConfigsService: UserConfigsService,
		private indexClient: IndexClient,
		private pilotClient: PilotClient
	) {
		super();
		if (!this.userConfigsService.inRoles(this.roles.Administrator)) {
			this.columnsToDisplay = this.columnsToDisplay.filter(column => column !== 'selecto-checko');
		}

		this.dataSource$ = combineLatest([
			this.processingManager.outOfSync$,
			this.processingManager.kbOutOfSync$,
			this.processingManager.currentFilterText$
		]).pipe(
			filter(([collection]) => !!collection),
			map(([logCollection, logCollection2, term]) => {
				let finalResults: CaseHealthView[] = [];
				logCollection.forEach(x => {
					finalResults.push({
						id: `${1}-${x.id}`,
						caseId: x.id,
						name: x.name,
						documentCount: x.documentCount,
						indexDocCount: x.indexDocCount,
						indexHealthType: 1
					});
				})
				logCollection2.forEach(x => {
					finalResults.push({
						id: `${2}-${x.id}`,
						caseId: x.id,
						name: x.name,
						documentCount: x.documentCount,
						indexDocCount: x.indexDocCount,
						indexHealthType: 2
					});
				})
				if (term) {
					finalResults = finalResults?.filter((item) => {
						return item.name?.toLowerCase().includes(term.toLowerCase());
					});
				}
				this.total = finalResults.length;
				finalResults = orderBy(finalResults, (item) => item.name.toLowerCase(), 'asc');

				return new MatTableDataSource(finalResults);
			})
		);

		this.activatedRoute.params.pipe(
			map((params) => params?.id)
		).subscribe((x) => {
			this.processingManager.setCurrentActiveStepId(x);
		});

	}

	public synchronize(): void {
		this.dataSource$.pipe(take(1)).subscribe((source) => {
			const selected = source.data.filter((item) => this.isSelected(item));
			const operations = selected.map((entry) => {
				if (entry.indexHealthType === 1) {
					return this.indexClient.synchronizeCaseIndex$(entry.caseId);
				}

				if(entry.indexHealthType === 2){
					const stepRequest = new SyncKnowledgeBaseRequest();
					stepRequest.stepParams = {
						targetCaseId: entry.caseId
					};
					return this.pilotClient.synchronizeKnowledgeBase$(stepRequest.stepParams.targetCaseId);
				}
				

			});

			forkJoin(operations).subscribe(() => {
				this.notificationManager.displaySnackBarMessage({
					message: `Attempting to synchronize ${operations.length} cases.`
				});
				this.selectedIds = [];
			});
		});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
		this.processingManager.setCurrentActiveStepId(null);
	}

	public trackByFn(index: number, item: ProcessingStep) {
		return `${item.id}`;
	}
}

export class CaseHealthView {
	id: string;
	caseId: string;
	name: string;
	documentCount: number;
	indexDocCount: number;
	indexHealthType: number;
}